import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Build,
  ContentCut,
  Weekend,
  Schedule,
} from '@mui/icons-material';
import { getAllTasks, getMyTasks, updateTaskStatus, getAllSofas, getAllUsers } from '../api';
import { formatDateOnly } from '../dateUtils';
import { canEditTaskStage, isTaskManagement, ROLE_STAGE } from '../taskPermissions';

const statusColors = {
  pending: '#6b7280',
  received: '#1e40af',
  in_progress: '#475569',
  completed: '#059669',
};

const statusLabels = {
  pending: 'Ожидает',
  received: 'Принято',
  in_progress: 'В работе',
  completed: 'Завершено',
};

export default function TasksListNew({ user }) {
  const isManagement = isTaskManagement(user?.role);
  const workerStage = ROLE_STAGE[user?.role];
  const [tasks, setTasks] = useState([]);
  const [sofas, setSofas] = useState([]);
  const [users, setUsers] = useState([]);
  const [tabValue, setTabValue] = useState(workerStage?.tab || 0);
  const [selectedTask, setSelectedTask] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    loadData();
  // Loading is intentionally keyed to identity and role, not the user object reference.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role]);

  const loadData = async () => {
    try {
      const [tasksRes, sofasRes, usersRes] = await Promise.all([
        isManagement ? getAllTasks() : getMyTasks(),
        getAllSofas(),
        isManagement ? getAllUsers() : Promise.resolve({ data: [] }),
      ]);
      setTasks(tasksRes.data);
      setSofas(sofasRes.data);
      setUsers(isManagement ? usersRes.data : [user].filter(Boolean));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleUpdateStatus = async (taskId, statusType, newStatus) => {
    const task = tasks.find(item => item.id === taskId);
    if (!canEditTaskStage(user, task, statusType)) return;
    // Оптимистично обновляем локально — без перезагрузки и прыжка скролла
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, [statusType]: newStatus } : t));
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask(prev => prev ? { ...prev, [statusType]: newStatus } : prev);
    }
    try {
      await updateTaskStatus(taskId, { [statusType]: newStatus });
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Ошибка обновления статуса: ' + (error.response?.data?.detail || error.message));
      await loadData();
    }
  };

  const getFilteredTasks = () => {
    let filtered = tasks;
    if (tabValue === 1) {
      // Каркасы
      filtered = tasks.filter(t => t.frame_worker_id);
    } else if (tabValue === 2) {
      // Пошив
      filtered = tasks.filter(t => t.sewer_id);
    } else if (tabValue === 3) {
      // Обстрел
      filtered = tasks.filter(t => t.upholsterer_id);
    }
    return filtered;
  };

  const TaskCard = ({ task }) => {
    const sofa = sofas.find(s => s.id === task.sofa_id);
    const frameWorker = users.find(u => u.id === task.frame_worker_id);
    const sewer = users.find(u => u.id === task.sewer_id);
    const upholsterer = users.find(u => u.id === task.upholsterer_id);

    const getProgress = () => {
      let completed = 0;
      let total = 3;
      if (task.frame_status === 'completed') completed++;
      if (task.sewing_status === 'completed') completed++;
      if (task.upholstery_status === 'completed') completed++;
      return (completed / total) * 100;
    };

    return (
      <Card role="button" tabIndex={0} aria-label={`Открыть задачу ${sofa?.number || task.id}`} sx={{ mb: 2, cursor: 'pointer' }} onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setSelectedTask(task);
          setOpenDialog(true);
        }
      }} onClick={() => {
        setSelectedTask(task);
        setOpenDialog(true);
      }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap" mb={1}>
            <Typography variant="h6">
              {sofa?.number || `Задача #${task.id}`}
            </Typography>
            <Chip label={`${Math.round(getProgress())}%`} size="small" color="primary" />
          </Box>

          <Typography variant="body2" color="textSecondary" gutterBottom>
            {sofa?.model?.name || 'Модель не указана'}
          </Typography>

          <LinearProgress variant="determinate" value={getProgress()} sx={{ my: 2 }} />

          <Grid container spacing={1}>
            {/* Каркас */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Build fontSize="small" color="action" />
                <Typography variant="body2">Каркас:</Typography>
                <Chip
                  label={statusLabels[task.frame_status]}
                  size="small"
                  sx={{ bgcolor: statusColors[task.frame_status], color: 'white', ml: 'auto' }}
                />
              </Box>
              {frameWorker && (
                <Typography variant="caption" color="textSecondary" display="block" ml={3}>
                  {frameWorker.full_name} | {task.frame_payment}₸
                </Typography>
              )}
            </Grid>

            {/* Пошив */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <ContentCut fontSize="small" color="action" />
                <Typography variant="body2">Пошив:</Typography>
                <Chip
                  label={statusLabels[task.sewing_status]}
                  size="small"
                  sx={{ bgcolor: statusColors[task.sewing_status], color: 'white', ml: 'auto' }}
                />
              </Box>
              {sewer && (
                <Typography variant="caption" color="textSecondary" display="block" ml={3}>
                  {sewer.full_name} | {task.sewing_payment}₸
                </Typography>
              )}
            </Grid>

            {/* Обстрел */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Weekend fontSize="small" color="action" />
                <Typography variant="body2">Обстрел:</Typography>
                <Chip
                  label={statusLabels[task.upholstery_status]}
                  size="small"
                  sx={{ bgcolor: statusColors[task.upholstery_status], color: 'white', ml: 'auto' }}
                />
              </Box>
              {upholsterer && (
                <Typography variant="caption" color="textSecondary" display="block" ml={3}>
                  {upholsterer.full_name} | {task.upholstery_payment}₸
                </Typography>
              )}
            </Grid>
          </Grid>

          {sofa?.delivery_date && (
            <Box display="flex" alignItems="center" gap={1} mt={2} p={1} bgcolor="warning.lighter" borderRadius={1}>
              <Schedule fontSize="small" color="warning" />
              <Typography variant="caption">
                Доставка: {formatDateOnly(sofa.delivery_date)}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const KanbanColumn = ({ status, title, tasks }) => (
    <Paper sx={{ p: 2, height: '100%', minWidth: 0, bgcolor: '#f5f5f5' }}>
      <Typography variant="h6" gutterBottom>
        {title}
        <Chip label={tasks.length} size="small" sx={{ ml: 1 }} />
      </Typography>
      <Box sx={{ mt: 2 }}>
        {tasks.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Нет задач
          </Typography>
        ) : tasks.map(task => (
          <TaskCard key={task.id} task={task} />
        ))}
      </Box>
    </Paper>
  );

  const filteredTasks = getFilteredTasks();

  // Определяем поле статуса в зависимости от вкладки
  const getStatusField = () => {
    if (tabValue === 2) return 'sewing_status';
    if (tabValue === 3) return 'upholstery_status';
    return 'frame_status';
  };

  const statusField = getStatusField();
  const getOverallStatus = (task) => {
    const statuses = [task.frame_status, task.sewing_status, task.upholstery_status];
    if (statuses.every(status => status === 'completed')) return 'completed';
    if (statuses.some(status => status === 'in_progress' || status === 'completed')) return 'in_progress';
    if (statuses.some(status => status === 'received')) return 'received';
    return 'pending';
  };

  // Группировка задач по статусам для Kanban
  const tasksByStatus = {
    pending: filteredTasks.filter(t => (tabValue === 0 ? getOverallStatus(t) : t[statusField]) === 'pending'),
    received: filteredTasks.filter(t => (tabValue === 0 ? getOverallStatus(t) : t[statusField]) === 'received'),
    in_progress: filteredTasks.filter(t => (tabValue === 0 ? getOverallStatus(t) : t[statusField]) === 'in_progress'),
    completed: filteredTasks.filter(t => (tabValue === 0 ? getOverallStatus(t) : t[statusField]) === 'completed'),
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Управление задачами
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Всего задач: {tasks.length} | В работе: {tasks.filter(t => t.frame_status === 'in_progress' || t.sewing_status === 'in_progress' || t.upholstery_status === 'in_progress').length}
          </Typography>
        </Box>
      </Box>

      {/* Вкладки */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          {isManagement && <Tab label="Все задачи" value={0} />}
          {(!workerStage || workerStage.tab === 1) && <Tab label="Каркасы" value={1} />}
          {(!workerStage || workerStage.tab === 2) && <Tab label="Пошив" value={2} />}
          {(!workerStage || workerStage.tab === 3) && <Tab label="Обстрел" value={3} />}
        </Tabs>
      </Paper>

      {/* Kanban доска */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} xl={3} sx={{ minWidth: 0 }}>
          <KanbanColumn
            status="pending"
            title="Ожидает"
            tasks={tasksByStatus.pending}
          />
        </Grid>
        <Grid item xs={12} md={6} xl={3} sx={{ minWidth: 0 }}>
          <KanbanColumn
            status="received"
            title="Принято"
            tasks={tasksByStatus.received}
          />
        </Grid>
        <Grid item xs={12} md={6} xl={3} sx={{ minWidth: 0 }}>
          <KanbanColumn
            status="in_progress"
            title="В работе"
            tasks={tasksByStatus.in_progress}
          />
        </Grid>
        <Grid item xs={12} md={6} xl={3} sx={{ minWidth: 0 }}>
          <KanbanColumn
            status="completed"
            title="Завершено"
            tasks={tasksByStatus.completed}
          />
        </Grid>
      </Grid>

      {/* Диалог детальной информации */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Детали задачи</DialogTitle>
        <DialogContent>
          {selectedTask && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {sofas.find(s => s.id === selectedTask.sofa_id)?.number || 'Диван'}
              </Typography>

              <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* Каркас */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Build /> Каркас
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          select
                          label="Статус"
                          value={selectedTask.frame_status}
                          disabled={!isManagement && !(workerStage?.status === 'frame_status' && selectedTask.frame_worker_id === user?.id)}
                          onChange={(e) => handleUpdateStatus(selectedTask.id, 'frame_status', e.target.value)}
                        >
                          <MenuItem value="pending">Ожидает</MenuItem>
                          <MenuItem value="received">Принято</MenuItem>
                          <MenuItem value="in_progress">В работе</MenuItem>
                          <MenuItem value="completed">Завершено</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Оплата"
                          value={selectedTask.frame_payment}
                          InputProps={{ readOnly: true, endAdornment: '₸' }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                          Исполнитель: {users.find(u => u.id === selectedTask.frame_worker_id)?.full_name || '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Пошив */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ContentCut /> Пошив
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          select
                          label="Статус"
                          value={selectedTask.sewing_status}
                          disabled={!isManagement && !(workerStage?.status === 'sewing_status' && selectedTask.sewer_id === user?.id)}
                          onChange={(e) => handleUpdateStatus(selectedTask.id, 'sewing_status', e.target.value)}
                        >
                          <MenuItem value="pending">Ожидает</MenuItem>
                          <MenuItem value="received">Принято</MenuItem>
                          <MenuItem value="in_progress">В работе</MenuItem>
                          <MenuItem value="completed">Завершено</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Оплата"
                          value={selectedTask.sewing_payment}
                          InputProps={{ readOnly: true, endAdornment: '₸' }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                          Исполнитель: {users.find(u => u.id === selectedTask.sewer_id)?.full_name || '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Обстрел */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Weekend /> Обстрел
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          select
                          label="Статус"
                          value={selectedTask.upholstery_status}
                          disabled={!isManagement && !(workerStage?.status === 'upholstery_status' && selectedTask.upholsterer_id === user?.id)}
                          onChange={(e) => handleUpdateStatus(selectedTask.id, 'upholstery_status', e.target.value)}
                        >
                          <MenuItem value="pending">Ожидает</MenuItem>
                          <MenuItem value="received">Принято</MenuItem>
                          <MenuItem value="in_progress">В работе</MenuItem>
                          <MenuItem value="completed">Завершено</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Оплата"
                          value={selectedTask.upholstery_payment}
                          InputProps={{ readOnly: true, endAdornment: '₸' }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                          Исполнитель: {users.find(u => u.id === selectedTask.upholsterer_id)?.full_name || '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
